import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolid, HandThumbDownIcon as HandThumbDownSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { chatbotApi } from '../lib/chatbot';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  messageId?: string; // Backend message ID for feedback
  feedback?: 'helpful' | 'not_helpful' | null;
}

export default function ChatbotWidget() {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(() => sessionStorage.getItem('chatbot-open') === 'true');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Persist open state across navigations
  useEffect(() => {
    sessionStorage.setItem('chatbot-open', String(isOpen));
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  if (!isAuthenticated) return null;

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await chatbotApi.sendMessage({
        message: trimmed,
        conversationId,
        context: { currentPage: location.pathname },
      });

      const { conversationId: convId, messageId, answer } = response.data;
      setConversationId(convId);

      const assistantMsg: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: answer,
        messageId,
        feedback: null,
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Something went wrong. Please try again.';
      const assistantMsg: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: errorMsg,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFeedback = async (msg: Message, type: 'helpful' | 'not_helpful') => {
    if (!msg.messageId || msg.feedback === type) return;

    try {
      await chatbotApi.submitFeedback(msg.messageId, type);
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, feedback: type } : m)),
      );
    } catch {
      toast.error('Failed to submit feedback');
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      <AnimatePresence mode="wait">
        {!isOpen ? (
          <motion.button
            key="toggle"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setIsOpen(true)}
            className="h-14 w-14 rounded-full bg-ocean-600 text-white shadow-lg hover:bg-ocean-700 flex items-center justify-center transition-colors"
            aria-label="Open support chat"
          >
            <ChatBubbleLeftRightIcon className="h-6 w-6" />
          </motion.button>
        ) : (
          <motion.div
            key="panel"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="w-[400px] h-[520px] rounded-xl bg-white border border-gray-200 shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-ocean-600 text-white shrink-0">
              <div className="flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5" />
                <span className="font-semibold text-sm">Clarid Support</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-md hover:bg-ocean-700 transition-colors"
                aria-label="Close chat"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Disclaimer — always visible */}
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 shrink-0">
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-snug">
                Product support only. I cannot provide compliance advice or interpret your data.
              </p>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <div className="text-center text-sm text-gray-400 mt-8">
                  <p className="font-medium text-gray-500 mb-1">Hi{user?.firstName ? `, ${user.firstName}` : ''}!</p>
                  <p>Ask me anything about using Clarid.</p>
                </div>
              )}

              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-last' : ''}`}>
                    <div
                      className={`px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                        msg.role === 'user'
                          ? 'bg-ocean-600 text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}
                    >
                      {msg.content}
                    </div>

                    {/* Feedback buttons for assistant messages */}
                    {msg.role === 'assistant' && msg.messageId && (
                      <div className="flex items-center gap-1 mt-1 ml-1">
                        <button
                          onClick={() => handleFeedback(msg, 'helpful')}
                          className={`p-0.5 rounded transition-colors ${
                            msg.feedback === 'helpful'
                              ? 'text-ocean-600'
                              : 'text-gray-300 hover:text-gray-500'
                          }`}
                          title="Helpful"
                        >
                          {msg.feedback === 'helpful' ? (
                            <HandThumbUpSolid className="h-3.5 w-3.5" />
                          ) : (
                            <HandThumbUpIcon className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleFeedback(msg, 'not_helpful')}
                          className={`p-0.5 rounded transition-colors ${
                            msg.feedback === 'not_helpful'
                              ? 'text-red-500'
                              : 'text-gray-300 hover:text-gray-500'
                          }`}
                          title="Not helpful"
                        >
                          {msg.feedback === 'not_helpful' ? (
                            <HandThumbDownSolid className="h-3.5 w-3.5" />
                          ) : (
                            <HandThumbDownIcon className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Typing indicator */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-100 rounded-lg rounded-bl-sm px-4 py-3">
                    <div className="flex gap-1">
                      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="border-t border-gray-200 px-3 py-2 shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value.slice(0, 2000))}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about Clarid features..."
                  rows={1}
                  disabled={isLoading}
                  className="flex-1 resize-none px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ maxHeight: '80px' }}
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  className="p-2 rounded-lg bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                  aria-label="Send message"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
